import { useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  InputAdornment, TextField, Tooltip, Typography,
} from '@mui/material'
import { Archive, ArchiveRestore, ChevronDown, ChevronRight, Download, FileUp, Languages, Pencil, Plus, Search } from 'lucide-react'
import { AppShell, Crumb, SkeletonCards, Surface, formatDate, saveAs, tokens } from '@agribot/polygon-checks-ui'
import {
  downloadTaxonomy, useAddCommodity, useAddIngredient, useDecideChange, useEditCommodity, useEditIngredient, useImportTaxonomy, useRoles,
  useTaxonomy, useTaxonomyChanges, type ApiCommodity, type ApiIngredient, type ImportResult, type TaxonomyChange, type TaxonomyCodes,
} from '../api'
import { PORTAL_LANGUAGES } from '@agribot/sourcing-model'
import { ImportDialog } from '../components/ImportDialog'

/** Outside codes as short labels: "EU 10", "EU additive 3a", "HS 2301.20". */
const CODE_LABELS: [keyof TaxonomyCodes, string][] = [
  ['eu_feed_catalogue', 'EU'], ['eu_feed_additive', 'EU additive'], ['hs', 'HS'], ['fao_cpc', 'FAO'], ['asfis', 'ASFIS'],
]
const CODE_HELP: Record<string, string> = {
  eu_feed_catalogue: 'EU Catalogue of feed materials (Regulation 68/2013): chapter or entry',
  eu_feed_additive: 'EU feed additive category (Regulation 1831/2003), e.g. 3a vitamins',
  hs: 'Harmonized System code (World Customs Organization)',
  fao_cpc: 'FAO / UN Central Product Classification',
  asfis: 'FAO ASFIS species code (fish)',
}

/** The supplier portal's languages besides English, for translated names. */
const LANGUAGES = PORTAL_LANGUAGES.filter((l) => l.code !== 'en').map((l) => ({ code: l.code, label: l.english }))

type Editing =
  | { kind: 'commodity'; item?: ApiCommodity }
  | { kind: 'ingredient'; commodityId: string; item?: ApiIngredient }
  | { kind: 'translate'; target: 'commodity' | 'ingredient'; item: ApiCommodity | ApiIngredient }

/**
 * Screen 13: the taxonomy, for R&D. Commodities and their ingredients as a
 * two-level list; add, rename, retire (never delete: answers already given
 * keep their meaning) and translate. Beside it, the changes suppliers have
 * proposed, to accept or decline. Anyone on staff can read; changing it
 * takes the R&D role.
 */
export function Taxonomy() {
  const { data, isPending, error } = useTaxonomy()
  const { data: changes } = useTaxonomyChanges()
  const canEdit = useRoles().includes('rnd')
  const [query, setQuery] = useState('')
  const [showRetired, setShowRetired] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<Editing | null>(null)
  const [importing, setImporting] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const download = async () => {
    setDownloadError(null)
    try {
      saveAs(await downloadTaxonomy(), 'taxonomy.csv')
    } catch (e) {
      setDownloadError((e as Error).message)
    }
  }

  const q = query.trim().toLowerCase()
  const groups = useMemo(() => {
    if (!data) return []
    const matches = (s: { name: string; code: string }) => !q || s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
    return data.commodities
      .filter((c) => showRetired || c.active)
      .map((c) => {
        const ingredients = data.ingredients.filter((f) => f.commodityId === c.id && (showRetired || f.active))
        const hits = matches(c) ? ingredients : ingredients.filter(matches)
        return { commodity: c, ingredients: hits, shown: matches(c) || hits.length > 0 }
      })
      .filter((g) => g.shown)
  }, [data, q, showRetired])
  const waiting = (changes ?? []).filter((c) => c.status === 'waiting')
  const nameOf = (id: string) => data?.commodities.find((c) => c.id === id)?.name ?? data?.ingredients.find((f) => f.id === id)?.name ?? id

  return (
    <AppShell
      crumbs={[<Crumb key="a">Sourcing</Crumb>, <Crumb key="b">Taxonomy</Crumb>]}
      actions={(
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => void download()}>Download spreadsheet</Button>
          {canEdit && <Button variant="outlined" startIcon={<FileUp size={16} />} onClick={() => setImporting(true)}>Import spreadsheet</Button>}
          {canEdit && <Button startIcon={<Plus size={16} />} onClick={() => setEditing({ kind: 'commodity' })}>Add commodity</Button>}
        </Box>
      )}
    >
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, py: 7 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>Taxonomy</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6, maxWidth: 680 }}>
          The commodities and their ingredients that suppliers describe their materials with. A fixed material uses only its own
          commodity’s ingredients. Retired entries stay on answers already given and are no longer offered.
          {!canEdit && ' You can read it; changing it takes the R&D role.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 4 }}>{(error as Error).message}</Alert>}
        {downloadError && <Alert severity="error" sx={{ mb: 4 }}>{downloadError}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 6, alignItems: 'start' }}>
          <Box>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', mb: 3 }}>
              <TextField
                placeholder="Search commodities and ingredients"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
                inputProps={{ 'aria-label': 'Search commodities and ingredients' }}
                sx={{ flex: 1 }}
              />
              <FormControlLabel control={<Checkbox checked={showRetired} onChange={(_, v) => setShowRetired(v)} />} label="Show retired" />
            </Box>
            {isPending && <SkeletonCards columns="1fr" />}
            {data && (
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mb: 2 }}>
                {data.commodities.filter((c) => c.active).length} commodities · {data.ingredients.filter((f) => f.active).length} ingredients in use
              </Typography>
            )}
            <Surface>
              {groups.map(({ commodity: c, ingredients }, i) => {
                const expanded = open.has(c.id) || Boolean(q)
                return (
                  <Box key={c.id} sx={{ borderTop: i ? `1px solid ${tokens.color.ink200}` : 0 }}>
                    <Row
                      expandable
                      expanded={expanded}
                      onToggle={() => setOpen((prev) => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n })}
                      code={c.code}
                      name={c.name}
                      detail={`${data!.ingredients.filter((f) => f.commodityId === c.id && f.active).length} ingredients${c.areaOrigins ? ' · origins drawn as areas' : ''}${c.supplyRequired ? ' · farms required' : ''}`}
                      active={c.active}
                      translations={Object.keys(c.nameI18n ?? {}).length}
                      codes={c.codes}
                      bold
                      edit={canEdit ? {
                        rename: () => setEditing({ kind: 'commodity', item: c }),
                        translate: () => setEditing({ kind: 'translate', target: 'commodity', item: c }),
                      } : undefined}
                      retire={canEdit ? { target: 'commodity', id: c.id, active: c.active } : undefined}
                    />
                    {expanded && (
                      <Box sx={{ backgroundColor: tokens.color.ink50, pb: 2 }}>
                        {ingredients.map((f) => (
                          <Row
                            key={f.id}
                            indent
                            code={f.code}
                            name={f.name}
                            active={f.active}
                            translations={Object.keys(f.nameI18n ?? {}).length}
                            codes={f.codes}
                            conversion={f.conversionPct ?? null}
                            edit={canEdit ? {
                              rename: () => setEditing({ kind: 'ingredient', commodityId: c.id, item: f }),
                              translate: () => setEditing({ kind: 'translate', target: 'ingredient', item: f }),
                            } : undefined}
                            retire={canEdit ? { target: 'ingredient', id: f.id, active: f.active } : undefined}
                          />
                        ))}
                        {canEdit && c.active && (
                          <Button variant="text" size="small" startIcon={<Plus size={14} />} onClick={() => setEditing({ kind: 'ingredient', commodityId: c.id })} sx={{ ml: 13, mt: 1 }}>
                            Add ingredient to {c.name}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                )
              })}
              {data && groups.length === 0 && (
                <Typography variant="body2" sx={{ p: 6, color: 'text.secondary' }}>
                  {q ? 'Nothing matches.' : 'No commodities yet. Add the first, or load the buyer’s spreadsheet.'}
                </Typography>
              )}
            </Surface>
          </Box>

          <Surface sx={{ p: 5 }}>
            <Typography variant="h4" component="h2" sx={{ mb: 1 }}>Proposed changes · {waiting.length}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              Suppliers propose an ingredient they could not find. Accepting adds it to the taxonomy straight away.
            </Typography>
            {waiting.length === 0 && <Typography variant="body2">Nothing waiting.</Typography>}
            {waiting.map((c) => <Proposal key={c.id} change={c} canDecide={canEdit} nameOf={nameOf} />)}
            {(changes ?? []).some((c) => c.status !== 'waiting') && (
              <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 4 }}>
                {(changes ?? []).filter((c) => c.status === 'accepted').length} accepted · {(changes ?? []).filter((c) => c.status === 'declined').length} declined earlier
              </Typography>
            )}
          </Surface>
        </Box>
      </Box>
      {editing && <EditDialog editing={editing} onClose={() => setEditing(null)} />}
      {importing && <ImportTaxonomy onClose={() => setImporting(false)} />}
    </AppShell>
  )
}

function Row({ code, name, detail, active, translations, codes, conversion, bold, indent, expandable, expanded, onToggle, edit, retire }: {
  code: string
  name: string
  detail?: string
  active: boolean
  translations: number
  codes?: TaxonomyCodes
  conversion?: number | null
  bold?: boolean
  indent?: boolean
  expandable?: boolean
  expanded?: boolean
  onToggle?: () => void
  edit?: { rename: () => void; translate: () => void }
  retire?: { target: 'commodity' | 'ingredient'; id: string; active: boolean }
}) {
  const editCommodity = useEditCommodity()
  const editIngredient = useEditIngredient()
  const toggle = () => {
    if (!retire) return
    const run = retire.target === 'commodity' ? editCommodity : editIngredient
    run.mutate({ id: retire.id, active: !retire.active })
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 4, pl: indent ? 13 : 4, minHeight: 52, opacity: active ? 1 : 0.6 }}>
      {expandable && (
        <IconButton size="small" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? `Hide ${name}'s ingredients` : `Show ${name}'s ingredients`}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
      )}
      <Box sx={{ font: `400 12px ${tokens.font.mono}`, color: 'text.secondary', width: 72, flex: 'none' }}>{code}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ font: `${bold ? 600 : 400} 14px ${tokens.font.sans}` }}>{name}{!active && ' (retired)'}</Box>
        {detail && <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>{detail}</Box>}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 260 }}>
        {conversion ? (
          <Tooltip title="Share of the farm crop this ingredient yields (supply capacity check)">
            <Box component="span" sx={{ height: 20, px: 1.5, display: 'inline-flex', alignItems: 'center', borderRadius: 999, backgroundColor: tokens.color.brandGreen100, color: '#1f4741', font: `500 11px ${tokens.font.mono}` }}>
              {conversion}% of crop
            </Box>
          </Tooltip>
        ) : null}
        {CODE_LABELS.filter(([k]) => codes?.[k]).map(([k, label]) => (
          <Tooltip key={k} title={CODE_HELP[k]}>
            <Box component="span" sx={{ height: 20, px: 1.5, display: 'inline-flex', alignItems: 'center', borderRadius: 999, backgroundColor: tokens.color.ink100, color: tokens.color.ink600, font: `500 11px ${tokens.font.mono}`, whiteSpace: 'nowrap' }}>
              {label} {codes![k]}
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Tooltip title={`${translations} of ${LANGUAGES.length} languages translated`}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, font: `400 12px ${tokens.font.sans}`, color: translations === LANGUAGES.length ? tokens.color.success : 'text.secondary' }}>
          <Languages size={14} strokeWidth={1.75} />{translations}/{LANGUAGES.length}
        </Box>
      </Tooltip>
      {edit && (
        <>
          <Tooltip title="Rename"><IconButton size="small" aria-label={`Rename ${name}`} onClick={edit.rename}><Pencil size={15} /></IconButton></Tooltip>
          <Tooltip title="Translations"><IconButton size="small" aria-label={`Translate ${name}`} onClick={edit.translate}><Languages size={15} /></IconButton></Tooltip>
        </>
      )}
      {retire && (
        <Tooltip title={retire.active ? 'Retire: no longer offered to suppliers' : 'Restore'}>
          <IconButton size="small" aria-label={retire.active ? `Retire ${name}` : `Restore ${name}`} onClick={toggle}>
            {retire.active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

function Proposal({ change, canDecide, nameOf }: { change: TaxonomyChange; canDecide: boolean; nameOf: (id: string) => string }) {
  const decide = useDecideChange()
  const [declining, setDeclining] = useState(false)
  const [comment, setComment] = useState('')
  const what = {
    add_ingredient: `New ingredient “${change.payload.name}” in ${nameOf(change.payload.commodity_id ?? '')}`,
    add_commodity: `New commodity “${change.payload.name}”`,
    rename_ingredient: `Rename ${nameOf(change.payload.id ?? '')} to “${change.payload.name}”`,
    rename_commodity: `Rename ${nameOf(change.payload.id ?? '')} to “${change.payload.name}”`,
  }[change.kind]
  return (
    <Box sx={{ py: 3, borderTop: `1px solid ${tokens.color.ink200}` }}>
      <Box sx={{ font: `500 14px ${tokens.font.sans}` }}>{what}</Box>
      <Box sx={{ font: `400 12px ${tokens.font.sans}`, color: 'text.secondary', mt: 0.5 }}>
        {change.proposedBy ?? 'Someone'}{change.supplier ? `, ${change.supplier}` : ''} · {formatDate(change.createdAt)}
      </Box>
      {change.reason && <Box sx={{ font: `400 13px ${tokens.font.sans}`, mt: 1.5 }}>“{change.reason}”</Box>}
      {decide.isError && <Alert severity="error" sx={{ mt: 2 }}>{(decide.error as Error).message}</Alert>}
      {canDecide && !declining && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button size="small" disabled={decide.isPending} onClick={() => decide.mutate({ id: change.id, status: 'accepted' })}>Accept</Button>
          <Button size="small" variant="outlined" onClick={() => setDeclining(true)}>Decline</Button>
        </Box>
      )}
      {declining && (
        <Box component="form" onSubmit={(e) => { e.preventDefault(); decide.mutate({ id: change.id, status: 'declined', comment }) }} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Why not? (sent to the supplier)" value={comment} onChange={(e) => setComment(e.target.value)} multiline minRows={2} InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-input': { height: 'auto', p: 0 }, '& .MuiOutlinedInput-root': { p: 2.5 } }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" size="small" disabled={decide.isPending}>Decline</Button>
            <Button size="small" variant="text" onClick={() => setDeclining(false)}>Cancel</Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

function EditDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const addCommodity = useAddCommodity()
  const editCommodity = useEditCommodity()
  const addIngredient = useAddIngredient()
  const editIngredient = useEditIngredient()
  const existing = editing.kind === 'translate' ? editing.item : editing.item
  const [code, setCode] = useState('')
  const [name, setName] = useState(existing?.name ?? '')
  const [areaOrigins, setAreaOrigins] = useState(editing.kind === 'commodity' ? editing.item?.areaOrigins ?? false : false)
  const [supplyRequired, setSupplyRequired] = useState(editing.kind === 'commodity' ? editing.item?.supplyRequired ?? false : false)
  const [names, setNames] = useState<Record<string, string>>(existing?.nameI18n ?? {})
  const [codes, setCodes] = useState<TaxonomyCodes>((editing.kind === 'translate' ? undefined : editing.item?.codes) ?? {})
  const codeKeys: (keyof TaxonomyCodes)[] = editing.kind === 'commodity' ? ['eu_feed_catalogue', 'hs'] : ['eu_feed_catalogue', 'eu_feed_additive', 'hs']
  const codesBody = Object.fromEntries(codeKeys.map((k) => [k, codes[k]?.trim() || null])) as Partial<Record<keyof TaxonomyCodes, string | null>>
  const [conversion, setConversion] = useState(editing.kind === 'ingredient' && editing.item?.conversionPct ? String(editing.item.conversionPct) : '')
  const conversionValue = conversion.trim() === '' ? null : Number(conversion)
  const conversionBad = conversionValue !== null && !(conversionValue > 0 && conversionValue <= 100)
  const running = [addCommodity, editCommodity, addIngredient, editIngredient]
  const busy = running.some((m) => m.isPending)
  const error = running.map((m) => m.error).find(Boolean) as Error | undefined
  const done = { onSuccess: onClose }

  const submit = () => {
    if (editing.kind === 'translate') {
      const nameI18n = Object.fromEntries(Object.entries(names).filter(([, v]) => v.trim()))
      if (editing.target === 'commodity') editCommodity.mutate({ id: editing.item.id, nameI18n }, done)
      else editIngredient.mutate({ id: editing.item.id, nameI18n }, done)
    } else if (editing.kind === 'commodity') {
      if (editing.item) editCommodity.mutate({ id: editing.item.id, name, areaOrigins, supplyRequired, codes: codesBody }, done)
      else addCommodity.mutate({ code, name, areaOrigins }, done)
    } else if (editing.item) editIngredient.mutate({ id: editing.item.id, name, codes: codesBody, conversionPct: conversionValue }, done)
    else addIngredient.mutate({ commodityId: editing.commodityId, code, name }, done)
  }

  const title = editing.kind === 'translate'
    ? `Translations of ${editing.item.name}`
    : `${editing.item ? 'Rename' : 'Add'} ${editing.kind}`
  const field = { '& .MuiOutlinedInput-input': { height: 40 } }
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); submit() }}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: '8px !important' }}>
          {editing.kind === 'translate' ? (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Suppliers see these in their language. Empty falls back to English.</Typography>
              {LANGUAGES.map((l) => (
                <TextField key={l.code} label={l.label} value={names[l.code] ?? ''} onChange={(e) => setNames((p) => ({ ...p, [l.code]: e.target.value }))} InputLabelProps={{ shrink: true }} sx={field} inputProps={{ lang: l.code }} />
              ))}
            </>
          ) : (
            <>
              {!editing.item && <TextField label="Code" value={code} onChange={(e) => setCode(e.target.value)} required helperText="Stable from year to year; it cannot change later." InputLabelProps={{ shrink: true }} sx={field} />}
              <TextField label="Name (English)" value={name} onChange={(e) => setName(e.target.value)} required autoFocus InputLabelProps={{ shrink: true }} sx={field} />
              {editing.item && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 3 }}>
                  {codeKeys.map((k) => (
                    <TextField key={k} label={CODE_LABELS.find(([x]) => x === k)![1]} value={codes[k] ?? ''} onChange={(e) => setCodes((c) => ({ ...c, [k]: e.target.value }))}
                      helperText={CODE_HELP[k]} InputLabelProps={{ shrink: true }} sx={field} />
                  ))}
                </Box>
              )}
              {editing.kind === 'ingredient' && editing.item && (
                <TextField
                  label="Conversion from farm crop (%)"
                  value={conversion}
                  onChange={(e) => setConversion(e.target.value.replace(',', '.'))}
                  error={conversionBad}
                  helperText={conversionBad ? 'Above 0 and up to 100.' : 'How much of the harvested crop becomes this ingredient, e.g. about 20% oil from palm fresh fruit bunches. Used to check origins against what their farms declare. Leave empty if unknown.'}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={field}
                />
              )}
              {editing.kind === 'commodity' && (
                <FormControlLabel
                  control={<Checkbox checked={areaOrigins} onChange={(_, v) => setAreaOrigins(v)} />}
                  label="Deforestation-sensitive: origins are drawn as areas and checked against forest maps"
                />
              )}
              {editing.kind === 'commodity' && editing.item && (
                <FormControlLabel
                  control={<Checkbox checked={supplyRequired} onChange={(_, v) => setSupplyRequired(v)} />}
                  label="Farms required: suppliers must link the cooperatives, farmers or plots behind each origin before submitting"
                />
              )}
            </>
          )}
          {error && <Alert severity="error">{error.message}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 6, pb: 5, gap: 2 }}>
          <Button variant="text" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || conversionBad || (editing.kind !== 'translate' && (!name.trim() || (!editing.item && !code.trim())))}>Save</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

/**
 * Loading a spreadsheet: checked first (nothing written), with what would
 * be added or changed and each problem by row; then imported in one go.
 */
function ImportTaxonomy({ onClose }: { onClose: () => void }) {
  return (
    <ImportDialog
      title="Import a taxonomy spreadsheet"
      run={useImportTaxonomy()}
      summary={(r: ImportResult) =>
        `${r.commodities.added} new and ${r.commodities.changed} changed commodities · ${r.ingredients.added} new and ${r.ingredients.changed} changed ingredients · ${r.commodities.unchanged + r.ingredients.unchanged} unchanged`}
      nothingToDo={(r) => r.commodities.added + r.commodities.changed + r.ingredients.added + r.ingredients.changed === 0}
      help={<>
        A .csv or .xlsx with one row per ingredient and its commodity: commodity_code, commodity_name, area_origins,
        ingredient_code, ingredient_name, and optionally eu_feed_catalogue, eu_feed_additive, hs_code, translations
        (ingredient_name_es, …) and active. “Download spreadsheet” gives the current taxonomy in exactly this layout.
        Nothing is deleted, and an ingredient never moves to another commodity.
      </>}
      onClose={onClose}
    />
  )
}
